import { extractEntity, extractList } from './tournamentsApi';

export const CLIENT_FORMS_PAGE_SIZE = 50;

export const EMPTY_PENDING_CLIENT_FORMS = {
  pending_client_forms: 0,
  pending_client_forms_by_champ: [],
};

const pendingClientFormsPayload = (data) => {
  const root = data && typeof data === 'object' ? data : {};
  if (root.pending_client_forms != null || Array.isArray(root.pending_client_forms_by_champ)) {
    return root;
  }
  const nested = root.data && typeof root.data === 'object' && !Array.isArray(root.data) ? root.data : null;
  if (nested && (nested.pending_client_forms != null || Array.isArray(nested.pending_client_forms_by_champ))) {
    return nested;
  }
  return null;
};

const champIdOfPendingRow = (item) => Number(
  item?.championship_id ?? item?.championat_id ?? item?.champ_id
);

export const extractPendingClientForms = (data) => {
  const payload = pendingClientFormsPayload(data);
  if (!payload) return null;

  const byChamp = (Array.isArray(payload.pending_client_forms_by_champ)
    ? payload.pending_client_forms_by_champ
    : [])
    .map((item) => ({
      championship_id: champIdOfPendingRow(item),
      count: Number(item?.count) || 0,
    }))
    .filter((item) => item.championship_id > 0);

  const totalRaw = Number(payload.pending_client_forms);
  return {
    pending_client_forms: Number.isFinite(totalRaw)
      ? totalRaw
      : byChamp.reduce((sum, item) => sum + item.count, 0),
    pending_client_forms_by_champ: byChamp,
  };
};

export const pendingClientFormsCountForChamp = (counts, champId) => {
  const byChamp = counts?.pending_client_forms_by_champ || [];
  const parsed = Number(champId);
  if (Number.isFinite(parsed) && parsed > 0) {
    const row = byChamp.find((item) => item.championship_id === parsed);
    if (row) return row.count;
    if (byChamp.length) return 0;
  }
  if (counts?.pending_client_forms != null) {
    return Number(counts.pending_client_forms) || 0;
  }
  return byChamp.reduce((sum, item) => sum + item.count, 0);
};

export const formatPendingClientFormsBadge = (count) => {
  const value = Number(count) || 0;
  if (value <= 0) return '';
  return value > 99 ? '99+' : String(value);
};

// UI: только новая / обработана. status=2 (отклонена) на бэке может остаться в старых данных.
export const CLIENT_FORM_STATUSES = [
  { value: 0, labelKey: 'cfStatusNew' },
  { value: 1, labelKey: 'cfStatusDone' },
];

export const CLIENT_FORM_STATUS_LABELS = [
  ...CLIENT_FORM_STATUSES,
  { value: 2, labelKey: 'cfStatusRejected' },
];

export const CLIENT_FORM_TYPES = [
  { value: 'championship_join', labelKey: 'cfTypeJoin' },
  { value: 'open_league', labelKey: 'cfTypeOpenLeague' },
  { value: 'register_team', labelKey: 'cfTypeRegisterTeam' },
];

const toNumber = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeClientForm = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const id = toNumber(raw.id);
  if (!id) return null;
  const status = toNumber(raw.status);
  return {
    ...raw,
    id,
    status: status == null ? 0 : status,
    championship_id: toNumber(raw.championship_id ?? raw.championat_id ?? raw.champ_id),
    championship_city_id: toNumber(raw.championship_city_id),
    // city / city_id — что указал пользователь в форме, не scope города
    city_id: toNumber(raw.city_id),
    user_id: toNumber(raw.user_id),
    processed_by: toNumber(raw.processed_by),
    form_type: raw.form_type || '',
    name: raw.name || '',
    phone: raw.phone || '',
    email: raw.email || '',
    city: raw.city || '',
    contact: raw.contact || '',
    vk_url: raw.vk_url || raw.vk || '',
    how_learned: raw.how_learned || '',
    experience: raw.experience || '',
    comment: raw.comment || '',
    championship_name: raw.championship_name || raw.championat_name || '',
    processed_by_name: raw.processed_by_name || '',
    processed_by_family: raw.processed_by_family || '',
    processed_at: raw.processed_at || null,
    created_at: raw.created_at || null,
    updated_at: raw.updated_at || null,
  };
};

export const extractClientFormsPage = (payload) => {
  const root = payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
    ? payload.data
    : payload;
  const items = extractList(root?.items ?? root?.forms ?? root)
    .map(normalizeClientForm)
    .filter(Boolean);
  const total = toNumber(root?.total);
  const limit = toNumber(root?.limit) || CLIENT_FORMS_PAGE_SIZE;
  const offset = toNumber(root?.offset) || 0;
  // Корень city_id — город scope (championats.city_id), не client_id
  return {
    items,
    total: total == null ? items.length : total,
    limit,
    offset,
    city_id: toNumber(root?.city_id),
  };
};

export const extractClientForm = (payload) => {
  if (Array.isArray(payload)) return normalizeClientForm(payload[0]);
  const entity = extractEntity(payload);
  return normalizeClientForm(entity);
};

export const clientFormTypeLabelKey = (formType) => {
  const found = CLIENT_FORM_TYPES.find((item) => item.value === formType);
  return found?.labelKey || 'cfTypeUnknown';
};

export const clientFormStatusLabelKey = (status) => {
  const found = CLIENT_FORM_STATUS_LABELS.find((item) => item.value === Number(status));
  return found?.labelKey || 'cfStatusUnknown';
};

export const clientFormProcessedBy = (item) => {
  const name = [item?.processed_by_family, item?.processed_by_name].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (item?.processed_by) return `#${item.processed_by}`;
  return '';
};

export const clientFormContact = (item) => {
  if (item?.phone) return item.phone;
  if (item?.email) return item.email;
  if (item?.contact) return item.contact;
  return '';
};

export const formatClientFormDate = (value, lang = 'ru') => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(lang === 'en' ? 'en-GB' : 'ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
